import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Terminal, LogIn, Eye, EyeOff, Fingerprint } from "lucide-react";
import { startAuthentication, browserSupportsWebAuthn } from "@simplewebauthn/browser";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Login() {
  const navigate = useNavigate();
  const { login, setSession } = useAuth();
  const [loading, setLoading] = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [needs2FA, setNeeds2FA] = useState(false);
  const [totpCode, setTotpCode] = useState("");
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  // Passkey-admin TOTP step
  const [passkeyPendingToken, setPasskeyPendingToken] = useState(null);

  const finishLogin = (data) => {
    setSession(data.access_token, data.user);
    if (data.requires_enrollment) {
      toast.warning("Add a passkey or 2FA to continue (15 minutes)");
      navigate("/");
    } else if (data.must_setup_2fa) {
      toast.warning("Admins must enable 2FA. Redirecting to setup…");
      navigate("/settings?tab=security");
    } else {
      toast.success("Welcome back!");
      navigate("/");
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (needs2FA && passkeyPendingToken) {
      return handlePasskeyTotp();
    }
    setLoading(true);
    try {
      const result = await login(loginForm.email, loginForm.password, needs2FA ? totpCode : null);
      if (result?.requires_2fa) {
        setNeeds2FA(true);
        toast.info("Enter your 2FA code to continue");
      } else if (result?.requires_enrollment) {
        toast.warning("Add a passkey or 2FA to continue (15 minutes)");
        navigate("/");
      } else if (result?.must_setup_2fa) {
        toast.warning("Admins must enable 2FA. Redirecting to setup…");
        navigate("/settings?tab=security");
      } else {
        toast.success("Welcome back!");
        navigate("/");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handlePasskeyLogin = async () => {
    if (!loginForm.email) {
      toast.error("Enter your email first, then use your passkey");
      return;
    }
    if (!browserSupportsWebAuthn()) {
      toast.error("This browser does not support passkeys");
      return;
    }
    setPasskeyLoading(true);
    try {
      const opts = (await axios.post(`${API}/auth/passkey/authentication/options`, { email: loginForm.email })).data;
      const assertion = await Promise.race([
        startAuthentication({ optionsJSON: opts }),
        new Promise((_, rej) => setTimeout(() => rej(new Error("Passkey timed out — try again")), 60000)),
      ]);
      const res = (await axios.post(`${API}/auth/passkey/authentication/verify`, { ...assertion, userId: opts.userId })).data;
      if (res.requires_2fa) {
        setPasskeyPendingToken(res.pending_token);
        setNeeds2FA(true);
        toast.info("Passkey verified — enter your 2FA code to finish");
      } else {
        finishLogin(res);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || "Passkey sign-in failed");
    } finally {
      setPasskeyLoading(false);
    }
  };

  const handlePasskeyTotp = async () => {
    setLoading(true);
    try {
      const res = (await axios.post(`${API}/auth/passkey/authentication/totp`, {
        pending_token: passkeyPendingToken, code: totpCode,
      })).data;
      finishLogin(res);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid 2FA code");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#09090b] p-4" data-testid="login-page">
      <div className="w-full max-w-md space-y-6">
        {/* Logo */}
        <div className="text-center">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-blue-500/20 to-cyan-500/20 border border-blue-500/20 flex items-center justify-center mb-4">
            <Terminal className="w-8 h-8 text-blue-400" />
          </div>
          <h1 className="text-3xl font-bold font-mono text-white">ServerAI</h1>
          <p className="text-zinc-400 mt-1">Server Administration AI Platform</p>
        </div>

        {/* Login Form */}
        <Card className="bg-[#0c0c0e]/80 border-white/5 backdrop-blur">
          <CardHeader className="pb-4 pt-6 text-center">
            <h2 className="text-lg font-medium text-white flex items-center justify-center gap-2">
              <LogIn className="w-4 h-4" /> Sign In
            </h2>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="login-email">Email</Label>
                <Input
                  id="login-email" type="email" value={loginForm.email}
                  onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                  placeholder="admin@example.com"
                  className="bg-black/50 border-white/10 text-white"
                  required disabled={needs2FA} data-testid="login-email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="login-password">Password</Label>
                <div className="relative">
                  <Input
                    id="login-password" type={showPassword ? "text" : "password"}
                    value={loginForm.password}
                    onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                    placeholder="Enter password" className="bg-black/50 border-white/10 text-white pr-10"
                    required disabled={needs2FA} data-testid="login-password"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              {needs2FA && (
                <div className="space-y-2">
                  <Label htmlFor="totp-code">2FA Code</Label>
                  <Input
                    id="totp-code" type="text" value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="Enter 6-digit code"
                    className="bg-black/50 border-white/10 text-white text-center tracking-widest text-lg font-mono"
                    maxLength={6} autoFocus required data-testid="login-totp-code"
                  />
                  <p className="text-xs text-zinc-500">Enter the code from your authenticator app</p>
                </div>
              )}
              <Button type="submit" disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white" data-testid="login-submit">
                {loading ? "Signing in..." : needs2FA ? "Verify & Sign In" : "Sign In"}
              </Button>
              {needs2FA && (
                <Button type="button" variant="ghost"
                  onClick={() => { setNeeds2FA(false); setTotpCode(""); setPasskeyPendingToken(null); }}
                  className="w-full text-zinc-400 hover:text-white" data-testid="login-back-btn">
                  Back to login
                </Button>
              )}
            </form>

            {!needs2FA && (
              <>
                <div className="flex items-center gap-3 my-4">
                  <div className="h-px flex-1 bg-white/10" />
                  <span className="text-xs text-zinc-600">or</span>
                  <div className="h-px flex-1 bg-white/10" />
                </div>
                <Button type="button" variant="outline"
                  onClick={handlePasskeyLogin} disabled={passkeyLoading}
                  className="w-full border-white/10 bg-black/30 text-white hover:bg-white/5"
                  data-testid="login-passkey-btn">
                  <Fingerprint className="w-4 h-4 mr-2 text-cyan-400" />
                  {passkeyLoading ? "Waiting for passkey…" : "Sign in with a passkey"}
                </Button>
                <p className="text-[11px] text-zinc-600 mt-2 text-center">
                  Enter your email above, then use your fingerprint, Face ID, or security key
                </p>
              </>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-xs text-zinc-600">
          Contact your administrator for account access
        </p>
      </div>
    </div>
  );
}
